import { useContext, useEffect, useState } from "react";

import { useMediaQuery, useTheme } from "@mui/system";
import { DataGrid, useGridApiRef } from "@mui/x-data-grid";
import DataGridContext from "core/DataGridContext";

import ResponsiveDataGrid from "./ResponsiveDataGrid";
import WithStickyHeader from "./WithStickyHeader";

const TilleyDataGrid = ({
  className = "",
  gridKey,
  pageSizeOptions = [5, 10, 25, 50, 100],
  defaultPageSize = 25,
  rows = [],
  columns = [],
  getRowId,
  columnVisibilityModel = {},
  isRowSelectable = () => true,
  checkboxSelection = false,
  disableRowSelectionOnClick,
  rowSelectionModel,
  onRowSelectionModelChange = () => {},
  getRowHeight = null,
  hideFooterPagination = false,
  hideFooterSelectedRowCount = false,
  sx = {},
  isLoading = false,
  isResponsiveShow = true,
  isScrollToTopOnPageChange = false,
  ...props
}) => {
  const apiRef = useGridApiRef();
  const { getGridState, setGridState } = useContext(DataGridContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  useEffect(() => {
    if (props?.getFilteredRows) {
      props.getFilteredRows.current = apiRef;
    }
  }, []);

  const initialState = getGridState()[gridKey] || {
    pageSize: defaultPageSize,
    sorting: null,
  };

  const [paginationModel, setPaginationModel] = useState({
    pageSize: initialState.pageSize || defaultPageSize,
    page: 0,
  });

  const [sortModel, setSortModel] = useState(
    initialState.sorting ? [initialState.sorting] : [] // Ensure an empty array if no sorting is defined
  );

  useEffect(() => {
    const savedState = getGridState()[gridKey];
    if (savedState) {
      setPaginationModel((prev) => ({
        ...prev,
        pageSize: savedState.pageSize || defaultPageSize,
      }));
      setSortModel(savedState.sorting ? [savedState.sorting] : []); // Validate sorting before setting
    }
  }, [getGridState, gridKey, defaultPageSize]);

  const handlePaginationModelChange = (model) => {
    setPaginationModel(model);
    setGridState(gridKey, { pageSize: model.pageSize });
  };

  const handleSortModelChange = (model) => {
    setSortModel(model);
    setGridState(gridKey, { sorting: model[0] || null }); // Save only the first sorting object or null
  };

  return (
    <WithStickyHeader className={className}>
      {isResponsiveShow && isMobile ? (
        <ResponsiveDataGrid
          rows={rows}
          columns={columns}
          getRowId={getRowId}
          loading={isLoading}
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationModelChange}
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          pageSizeOptions={pageSizeOptions}
          isScrollToTopOnPageChange={isScrollToTopOnPageChange}
        />
      ) : (
        <DataGrid
          rows={rows}
          apiRef={apiRef}
          columns={columns}
          loading={isLoading}
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationModelChange}
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          pageSizeOptions={pageSizeOptions}
          getRowId={getRowId}
          autoHeight
          disableVirtualization
          sx={sx}
          columnVisibilityModel={columnVisibilityModel}
          isRowSelectable={isRowSelectable}
          checkboxSelection={checkboxSelection}
          disableRowSelectionOnClick={disableRowSelectionOnClick}
          rowSelectionModel={rowSelectionModel}
          onRowSelectionModelChange={onRowSelectionModelChange}
          getRowHeight={getRowHeight}
          hideFooterPagination={hideFooterPagination || false}
          hideFooterSelectedRowCount={hideFooterSelectedRowCount || false}
          {...props}
        />
      )}
    </WithStickyHeader>
  );
};

export default TilleyDataGrid;
